import React, { useState, useEffect } from 'react';
import {
  Database,
  HardDrive,
  Activity,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Trash2,
  Edit2
} from 'lucide-react';
import { databaseService, minioService, appsService } from '../services/api';
import toast from 'react-hot-toast';

function SystemStatus() {
  const [dbConnection, setDbConnection] = useState(null);
  const [databases, setDatabases] = useState([]);
  const [buckets, setBuckets] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState({ db: true, minio: true, apps: true });
  const [deleteModal, setDeleteModal] = useState({ show: false, type: null, item: null, error: null });
  const [renameModal, setRenameModal] = useState({ show: false, oldName: '' });
  const [newDbName, setNewDbName] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadSystemStatus();
  }, []);

  const loadSystemStatus = async () => {
    setLoading({ db: true, minio: true, apps: true });

    // Load apps info to check for usage
    try {
      const appsData = await appsService.getAll();
      setApps(appsData.apps || []);
    } catch (error) {
      console.error('Apps load error:', error);
    } finally {
      setLoading(prev => ({ ...prev, apps: false }));
    }

    // Load database info
    try {
      const [connectionData, databasesData] = await Promise.allSettled([
        databaseService.testConnection(),
        databaseService.listDatabases()
      ]);

      if (connectionData.status === 'fulfilled') {
        setDbConnection(connectionData.value.result);
      } else {
        setDbConnection({ success: false, error: connectionData.reason.message });
      }

      if (databasesData.status === 'fulfilled') {
        setDatabases(databasesData.value.databases);
      }
    } catch (error) {
      console.error('Database status error:', error);
    } finally {
      setLoading(prev => ({ ...prev, db: false }));
    }

    // Load MinIO info
    try {
      const bucketsData = await minioService.listBuckets();
      setBuckets(bucketsData.buckets);
    } catch (error) {
      console.error('MinIO status error:', error);
    } finally {
      setLoading(prev => ({ ...prev, minio: false }));
    }
  };

  const handleDeleteDatabase = async (name) => {
    if (name === 'postgres') {
      toast.error('The core postgres database cannot be deleted.');
      return;
    }

    try {
      setActionLoading(true);
      const result = await databaseService.deleteDatabase(name);
      if (result.result.deleted) {
        toast.success(`Database ${name} deleted successfully`);
        loadSystemStatus();
      } else {
        toast.error(`Failed to delete database ${name}`);
      }
    } catch (error) {
      toast.error(`Error deleting database: ${error.message}`);
    } finally {
      setActionLoading(false);
      setDeleteModal({ show: false, type: null, item: null, error: null });
    }
  };

  const handleRenameDatabase = async (e) => {
    e.preventDefault();
    if (!newDbName.trim()) {
      toast.error('New database name is required');
      return;
    }
    
    if (newDbName.trim() === renameModal.oldName) {
      toast.error('New name must be different from current name');
      return;
    }

    try {
      setActionLoading(true);
      const result = await databaseService.renameDatabase(renameModal.oldName, newDbName.trim());
      if (result.result.renamed) {
        toast.success(`Database ${renameModal.oldName} renamed to ${newDbName.trim()}`);
        loadSystemStatus();
        setRenameModal({ show: false, oldName: '' });
        setNewDbName('');
      } else {
        toast.error(`Failed to rename database: ${result.result.message}`);
      }
    } catch (error) {
      toast.error(`Error renaming database: ${error.response?.data?.error || error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteBucket = async (name, force = false) => {
    try {
      setActionLoading(true);
      const result = await minioService.deleteBucket(name, force);
      if (result.result.deleted) {
        toast.success(`Bucket ${name} deleted successfully`);
        loadSystemStatus();
        setDeleteModal({ show: false, type: null, item: null, error: null });
      } else {
        toast.error(`Failed to delete bucket ${name}`);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      if (errorMessage.includes('bucket is not empty')) {
        setDeleteModal(prev => ({ ...prev, error: 'NotEmpty' }));
      } else {
        toast.error(`Error deleting bucket: ${errorMessage}`);
        setDeleteModal({ show: false, type: null, item: null, error: null });
      }
    } finally {
      setActionLoading(false);
    }
  };

  const isDatabaseUsed = (dbName) => {
    if (dbName === 'postgres') return true;
    return apps.some(app => app.config?.DB_DATABASE === dbName);
  };

  const isBucketUsed = (bucketName) => {
    // Check if bucket follows app naming convention: appname-public or appname-private
    return apps.some(app =>
      bucketName === `${app.name}-public` || bucketName === `${app.name}-private`
    );
  };

  const StatusCard = ({ title, status, details, icon: Icon }) => (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="p-5">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <Icon className={`h-6 w-6 ${status ? 'text-green-600' : 'text-red-600'}`} />
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
              <dd className="flex items-center mt-1">
                <div className="text-lg font-medium text-gray-900">
                  {status ? 'Connected' : 'Disconnected'}
                </div>
                <div className="ml-2">
                  {status ? (
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-red-600" />
                  )}
                </div>
              </dd>
              {details && (
                <dd className="text-sm text-gray-500 mt-1">{details}</dd>
              )}
            </dl>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            System Status
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Monitor system components and infrastructure health
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <button
            onClick={loadSystemStatus}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <StatusCard
          title="Database Connection"
          status={dbConnection?.success}
          details={dbConnection?.success ? `PostgreSQL ${dbConnection.version?.split(' ')[1]}` : dbConnection?.error}
          icon={Database}
        />
        <StatusCard
          title="MinIO Storage"
          status={buckets.length > 0}
          details={`${buckets.length} bucket${buckets.length !== 1 ? 's' : ''} available`}
          icon={HardDrive}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Databases */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Databases
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              List of all PostgreSQL databases
            </p>
          </div>
          {loading.db ? (
            <div className="px-4 py-5 flex justify-center">
              <div className="loading-spinner"></div>
            </div>
          ) : (
            <ul role="list" className="divide-y divide-gray-200">
              {databases.map((db) => (
                <li key={db.name} className="px-4 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <Database className="h-5 w-5 text-gray-400" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {db.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          Size: {db.size}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      {!isDatabaseUsed(db.name) && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
                          Unused
                        </span>
                      )}
                      {db.name !== 'postgres' && (
                        <button
                          onClick={() => {
                            setRenameModal({ show: true, oldName: db.name });
                            setNewDbName(db.name);
                          }}
                          className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-blue-600 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          title="Rename database"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => setDeleteModal({ show: true, type: 'database', item: db.name })}
                        disabled={db.name === 'postgres'}
                        className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-30"
                        title={db.name === 'postgres' ? "Cannot delete core database" : "Delete database"}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Storage Buckets */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Storage Buckets
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              MinIO S3 buckets and their information
            </p>
          </div>
          {loading.minio ? (
            <div className="px-4 py-5 flex justify-center">
              <div className="loading-spinner"></div>
            </div>
          ) : (
            <ul role="list" className="divide-y divide-gray-200">
              {buckets.map((bucket) => (
                <li key={bucket.name} className="px-4 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <HardDrive className="h-5 w-5 text-gray-400" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {bucket.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          Created: {new Date(bucket.creationDate).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-sm text-gray-500">
                        {bucket.name.includes('-public') && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Public
                          </span>
                        )}
                        {bucket.name.includes('-private') && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Private
                          </span>
                        )}
                      </div>
                      {!isBucketUsed(bucket.name) && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
                          Unused
                        </span>
                      )}
                      <button
                        onClick={() => setDeleteModal({ show: true, type: 'bucket', item: bucket.name, error: null })}
                        className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        title="Delete bucket"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* System Information */}
      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            System Information
          </h3>
          <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
            <div>
              <dt className="text-sm font-medium text-gray-500">Database Server</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {dbConnection?.success ? (
                  <span className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                    PostgreSQL Connected
                  </span>
                ) : (
                  <span className="flex items-center">
                    <AlertCircle className="h-4 w-4 text-red-600 mr-2" />
                    Connection Failed
                  </span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Storage Server</dt>
              <dd className="mt-1 text-sm text-gray-900">
                <span className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                  MinIO S3 Compatible
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Total Databases</dt>
              <dd className="mt-1 text-sm text-gray-900">{databases.length}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Total Buckets</dt>
              <dd className="mt-1 text-sm text-gray-900">{buckets.length}</dd>
            </div>
            {dbConnection?.currentTime && (
              <div className="sm:col-span-2">
                <dt className="text-sm font-medium text-gray-500">Database Server Time</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(dbConnection.currentTime).toString()}
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => !actionLoading && setDeleteModal({ show: false, type: null, item: null, error: null })}
          />
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6 z-10">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  Delete {deleteModal.type === 'database' ? 'Database' : 'Bucket'}
                </h3>
                <p className="text-sm text-gray-500">
                  Are you sure you want to delete <span className="font-semibold text-gray-900">{deleteModal.item}</span>?
                  {deleteModal.error === 'NotEmpty' ? (
                    <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-red-700 font-medium">This bucket is not empty!</p>
                      <p className="text-red-600 mt-1">Force deleting will permanently remove all files inside this bucket.</p>
                    </div>
                  ) : (
                    " This action cannot be undone and all data contained within will be permanently lost."
                  )}
                </p>
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setDeleteModal({ show: false, type: null, item: null, error: null })}
                disabled={actionLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (deleteModal.type === 'database') {
                    handleDeleteDatabase(deleteModal.item);
                  } else {
                    handleDeleteBucket(deleteModal.item, deleteModal.error === 'NotEmpty');
                  }
                }}
                disabled={actionLoading}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
              >
                {actionLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  deleteModal.error === 'NotEmpty' ? 'Force Delete All' : 'Yes, Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Confirmation Modal */}
      {renameModal.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => !actionLoading && setRenameModal({ show: false, oldName: '' })}
          />
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6 z-10">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Rename Database: {renameModal.oldName}
            </h3>
            <form onSubmit={handleRenameDatabase}>
              <div className="mb-4">
                <label htmlFor="newDbName" className="block text-sm font-medium text-gray-700">
                  New Database Name
                </label>
                <input
                  type="text"
                  id="newDbName"
                  value={newDbName}
                  onChange={(e) => setNewDbName(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  pattern="^[a-zA-Z][a-zA-Z0-9_-]*$"
                  title="Must start with a letter and contain only letters, numbers, hyphens, and underscores."
                  required
                  disabled={actionLoading}
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setRenameModal({ show: false, oldName: '' })}
                  disabled={actionLoading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoading}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {actionLoading ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Renaming...
                    </>
                  ) : (
                    'Rename'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default SystemStatus;